import { useEffect, useRef } from 'react'
/**
 * The message list (prototype `vMail`'s `.mlistcol`).
 *
 * Each row carries the three things that decide whether to open it: who it's from, what it's
 * about, and whether it's already tied to a requirement. The `[REQ-####]` badge is that last
 * one — it's how a supplier's reply is recognised as belonging to a job in flight.
 *
 * ---- Outlook-density redesign (defect: the list read as noise, not a scannable queue) ----
 *
 * Every message in this mailbox arrives forwarded by the same internal address, which used to
 * make three things true on every single row: the avatar was the same coloured "MA" circle, the
 * preview line was the identical "---------- Forwarded message --------- From: …" boilerplate
 * (`plainPreview(body_html)` has no way to know that's not the message), and the subject wrapped
 * to two lines, pushing the timestamp off screen. None of that helped a purchaser decide which of
 * twenty rows to open.
 *
 * The fix has two parts:
 *  - `displaySender` (`lib/mail.ts`) parses the quoted `From:` header out of the forwarded body
 *    and shows the actual customer, falling back to the real `from`/`from_name` for the genuine
 *    non-forwarded mail this inbox also receives (Google alerts, a cPanel notice) — never a
 *    guess. The avatar is dropped rather than kept pointed at a name that's now wrong for most
 *    rows: a 38px circle that read "MA" on every row wasn't identity, it was wallpaper.
 *  - `meaningfulPreview` (`lib/mail.ts`) strips that same forwarded header block so the preview
 *    shows what the customer actually wrote, and it now sits on the SAME line as the subject
 *    (one `text-overflow: ellipsis` span, no wrap) so the row is two lines tall, not four, and
 *    the timestamp never gets pushed out of view.
 */

import { Badge } from '../../components/Badge'
import { describeProcessed, latestProcessed } from '../../lib/mailProcessed'
import { MAIL_KIND_ORDER, MAIL_KINDS, type MailKind } from '../../lib/mailKinds'
import { useNavigate } from 'react-router-dom'
import type { EnquirySummary } from '../../lib/types'
import { effectiveVerdict, readsAsSupplierReply } from '../../lib/mailVerdictOverride'
import { useVerdictOverride } from './useVerdictOverride'
import { formatTime, formatAge } from '../../lib/format'
import { displaySender, emptyFolderNote, meaningfulPreview, type MailParticipant } from '../../lib/mail'
import {
  classifyRfqMail,
  confidencePercent,
  explainClassification,
  isOwnOutgoingRfq,
  repliedEnquiryId,
} from '../../lib/mailClassify'
import type { MailFolder, MailMessage } from '../../lib/types'

/** Today shows a clock; anything older shows its age — the same rule a mail client uses. */
function timeLabel(iso: string): string {
  const age = formatAge(iso)
  return age === 'today' ? formatTime(iso) : age
}

/**
 * The RFQ-likelihood badge. A `not-rfq` verdict renders NOTHING — a list where every row wears a
 * badge is a list where the badge means nothing, and the useful signal is "this one is worth
 * opening". The tooltip always carries the signals that fired plus the "keyword score, not a
 * trained model" caveat, so the percentage is never presented as an oracle
 * (`lib/mailClassify.ts`, module header).
 */
function RfqBadge({ mail }: { mail: MailMessage }) {
  // A user's correction wins over both the reply detection and the score below. Subscribed rather
  // than read once, so correcting a mail in the reader redraws this badge immediately.
  const override = useVerdictOverride(mail.id)
  // A supplier answering one of OUR RFQs is a different thing from a customer sending a new
  // enquiry, and the RFQ-confidence score cannot tell them apart — a reply quoting our own
  // request back scores high on exactly the same keywords. This is knowable without a model:
  // every RFQ and reminder we send carries `[<enquiry-id>]` in the subject and a reply keeps it.
  // Shown INSTEAD of the confidence badge, because "RFQ 87%" on a supplier's answer invites
  // someone to raise a second enquiry from it.
  // Our own RFQ, fetched back into the inbox through the purchaser's mailbox (2026-09-19). Not
  // a reply and not an enquiry to create — said as what it is.
  if (mail.folder !== 'sent' && isOwnOutgoingRfq(mail.subject)) {
    return (
      <Badge tone="muted" title="Our own RFQ — the Cc copy the purchaser's mailbox received. Not a reply; nothing to create.">
        our RFQ{repliedEnquiryId(mail.subject) ? ` · ${repliedEnquiryId(mail.subject)}` : ''}
      </Badge>
    )
  }
  if (readsAsSupplierReply(mail)) {
    const enquiry = repliedEnquiryId(mail.subject)
    return (
      <Badge
        tone="violet"
        title={
          enquiry
            ? `A supplier's reply to ${enquiry} — matched on the subject tag we send, not by AI. Read it on the Supplier Quotations screen.`
            : "A supplier's reply — matched on the subject tag we send, not by AI."
        }
      >
        reply{enquiry ? ` · ${enquiry}` : ''}
      </Badge>
    )
  }

  const result = classifyRfqMail(mail)
  const verdict = effectiveVerdict(mail.id, result.verdict)
  if (verdict === 'not-rfq') return null
  // Corrected TO an RFQ: the score is no longer what is being asserted, so it is not shown as one.
  if (override === 'rfq') {
    return (
      <Badge tone="info" title="You marked this as an RFQ — a correction to the keyword score, held for this session only.">
        RFQ · marked by you
      </Badge>
    )
  }
  const isRfq = verdict === 'rfq'
  return (
    <Badge tone={isRfq ? 'ok' : 'warn'} title={explainClassification(result)}>
      {isRfq ? 'RFQ' : 'RFQ?'} {confidencePercent(result.confidence)}
    </Badge>
  )
}

export function MessageList({
  title,
  folder,
  messages,
  selectedId,
  search,
  isPending,
  onReachEnd,
  loadingMore = false,
  hasMore = false,
  participants,
  addressedTo,
  onSearch,
  onAddressedTo,
  addressLocked = false,
  onSelect,
  onToggleFlag,
  processedByMail,
  kindFilter = null,
  kindCounts,
  onKindFilter,
  kindOf,
}: {
  title: string
  /** Which folder is open — only the empty state reads it (`emptyFolderNote`). */
  folder: MailFolder
  messages: MailMessage[]
  /**
   * `Communication id → enquiries already created from it` (`lib/mailProcessed.ts`). Optional so
   * every other caller and the tests are unaffected; absent simply means no badge.
   */
  processedByMail?: ReadonlyMap<string, EnquirySummary[]>
  /** The kind chips — see `lib/mailKinds.ts`. `null` is "all". */
  kindFilter?: MailKind | null
  kindCounts?: ReadonlyMap<MailKind, number>
  onKindFilter?: (kind: MailKind | null) => void
  /** Each mail's kind, for the row badge — "auto-created" reads differently from "✓ ENQ-…". */
  kindOf?: ReadonlyMap<string, MailKind>
  selectedId: string | null
  search: string
  isPending: boolean
  /** Called when the bottom of the list comes into view — the next page (2026-09-25). */
  onReachEnd?: () => void
  /** A page is on its way; the list says so rather than looking finished. */
  loadingMore?: boolean
  /** Whether the server might still have older mail — a full last page. */
  hasMore?: boolean
  participants: MailParticipant[]
  addressedTo: string | null
  onSearch: (value: string) => void
  onAddressedTo: (address: string | null) => void
  /** A purchaser's view is their own mail only — no address control (2026-09-21). */
  addressLocked?: boolean
  onSelect: (id: string) => void
  onToggleFlag: (id: string) => void
}) {
  const navigate = useNavigate()
  return (
    <div className="mlistcol">
      <div className="mlisthead">
        <div className="lh">
          <b>{title}</b>
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>{messages.length}</span>
        </div>
        <input
          className="msearch"
          type="text"
          placeholder="🔍  Search mail"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
        {/*
          Narrow to the mail one address is on — the purchaser's "only what I'm copied on" view.
          Options come from the whole mailbox, so picking one never removes the way back
          (`useMailParticipants`).

          Shown whenever a filter is set or there is more than one correspondent. The `> 1` test
          alone hid the control on the live mailbox, where every message currently shares one
          recipient — but a purchaser who has set their address needs to see (and be able to clear)
          the filter that is narrowing their list, even when it matches nothing.
        */}
        {addressLocked ? (
          <div style={{ fontSize: 11.5, color: 'var(--muted)', padding: '4px 2px' }} title="You see the mail you are on — To or Cc. A purchase manager sees everyone's.">
            ✉ Your mail{addressedTo ? ` · ${addressedTo}` : ''}
          </div>
        ) : participants.length > 1 || addressedTo ? (
          <select
            className="msearch"
            aria-label="Show only mail addressed to"
            title="Filter by To/Cc. A view preference — everyone still has access to the same mailbox."
            value={addressedTo ?? ''}
            onChange={(e) => onAddressedTo(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">✉︎ Addressed to anyone</option>
            {/* A set address that appears on no message would otherwise vanish from the select
                and silently reset the filter to "anyone". */}
            {addressedTo && !participants.some((p) => p.address.toLowerCase() === addressedTo.toLowerCase()) ? (
              <option value={addressedTo}>{addressedTo} (0)</option>
            ) : null}
            {participants.map((participant) => (
              <option key={participant.address} value={participant.address}>
                {participant.address} ({participant.count})
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {onKindFilter && kindCounts ? (
        <div className="mailkinds" role="tablist" aria-label="Filter by kind">
          <button type="button" className={`chip${kindFilter === null ? ' on' : ''}`} onClick={() => onKindFilter(null)}>
            All <span className="n">{[...kindCounts.values()].reduce((a, b) => a + b, 0)}</span>
          </button>
          {MAIL_KIND_ORDER.filter((k) => (kindCounts.get(k) ?? 0) > 0 || kindFilter === k).map((k) => (
            <button
              key={k}
              type="button"
              className={`chip${kindFilter === k ? ' on' : ''}`}
              title={MAIL_KINDS[k].hint}
              onClick={() => onKindFilter(kindFilter === k ? null : k)}
            >
              {MAIL_KINDS[k].label} <span className="n">{kindCounts.get(k) ?? 0}</span>
            </button>
          ))}
        </div>
      ) : null}

      {isPending && messages.length === 0 ? (
        <div className="pad" style={{ color: 'var(--muted)' }}>
          Loading…
        </div>
      ) : null}

      {!isPending && messages.length === 0 ? (
        <div className="pad" style={{ color: 'var(--muted)' }}>
          {/* Name whichever filter emptied the list, or the user is left guessing which to clear. */}
          {search
            ? `No mail matching “${search}”${addressedTo ? ` addressed to ${addressedTo}` : ''}${kindFilter ? ` under “${MAIL_KINDS[kindFilter].label}”` : ''}.`
            : addressedTo
              ? `No mail addressed to ${addressedTo}.`
              : emptyFolderNote(folder)}
        </div>
      ) : null}

      {messages.map((mail) => {
        const sender = displaySender(mail)
        const preview = meaningfulPreview(mail.body_html || mail.preview)
        const processed = processedByMail?.get(mail.id) ?? []
        const processedLabel = describeProcessed(processed)
        return (
          // A div, not a button: the flag control inside it is itself interactive, and
          // interactive content nested in a button is invalid and unreliable for screen readers.
          <div
            key={mail.id}
            /* The mail's own id on the row — the only way a test (or a support question) can
               tell two rows apart when their sender, subject and age all read the same. */
            data-mail-id={mail.id}
            role="button"
            tabIndex={0}
            className={`mitem${mail.unread ? ' unread' : ''}${selectedId === mail.id ? ' sel' : ''}`}
            onClick={() => onSelect(mail.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelect(mail.id)
              }
            }}
          >
            <div className="micontent">
              <div className="top">
                <span
                  className="nm"
                  // The real sender/email always available on hover, even when we're showing the
                  // forwarded customer instead of who actually dropped this in the mailbox —
                  // provenance is one hover away, never hidden.
                  title={sender.forwarded ? `Forwarded by ${mail.from_name} <${mail.from}>` : sender.email}
                >
                  {sender.name}
                </span>
                <span className="tm">{timeLabel(mail.received_at)}</span>
              </div>
              <div className="sjrow">
                {/*
                  ALREADY PROCESSED — the enquiry number, right on the row.

                  Derived from the enquiries themselves, so it cannot claim a mail is done when its
                  enquiry has been deleted. `ok`-toned because that is the reassuring state: the
                  work is done and here is where it went. The count comes first when there are
                  several — one mail on this bench has twenty-four, and printing them all would
                  bury the subject.

                  Distinct from `mail.enquiry_id`, which is the `[REQ-####]` SUBJECT TAG on replies
                  — that says "this mail mentions an enquiry", this says "an enquiry was made FROM
                  this mail". A reply can have the first and not the second.
                */}
                {processedLabel ? (
                  /*
                    The badge OPENS the enquiry (Akhil, 2026-09-18: an enquiry created
                    automatically needs a way in) — the newest one, on the RFQ builder.
                    `stopPropagation`, or the click would select the mail instead.
                  */
                  <span
                    role="link"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      const latest = latestProcessed(processed)
                      if (latest) navigate(`/rfq/${latest.id}`)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        const latest = latestProcessed(processed)
                        if (latest) navigate(`/rfq/${latest.id}`)
                      }
                    }}
                  >
                    {kindOf?.get(mail.id) === 'auto-created' ? (
                      // Teal, not violet — violet is the supplier-reply badge (Akhil, 2026-09-21).
                      <Badge tone="teal" title={`Bosun created ${processedLabel} from this mail without a person — open it and check the header before sending an RFQ`}>
                        ⚡ auto · {processedLabel} →
                      </Badge>
                    ) : (
                      <Badge
                        tone="ok"
                        title={`Open on the RFQ builder — made into ${processed.length === 1 ? 'enquiry' : 'enquiries'} ${processed.map((e) => e.id).join(', ')}`}
                      >
                        ✓ {processedLabel} →
                      </Badge>
                    )}
                  </span>
                ) : null}
                {mail.enquiry_id ? <Badge tone="info">{mail.enquiry_id}</Badge> : null}
                <RfqBadge mail={mail} />
                <span className="sj">{mail.subject}</span>
                {preview ? <span className="pv"> — {preview}</span> : null}
              </div>
            </div>
            <div className="miactions">
              {mail.attachments.length > 0 ? (
                <span className="miatt" title={`${mail.attachments.length} attachment(s)`}>
                  📎 {mail.attachments.length}
                </span>
              ) : null}
              <button
                type="button"
                className={`iflag${mail.flagged ? ' on' : ''}`}
                aria-label={mail.flagged ? 'Remove flag' : 'Flag'}
                // Must not bubble into opening the row.
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleFlag(mail.id)
                }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                {mail.flagged ? '⚑' : '⚐'}
              </button>
            </div>
          </div>
        )
      })}

      {/*
        The bottom of the list. `mail.get_emails` caps a page at 100 rows and reports no grand
        total (measured 2026-09-25), so the list grows by fetching the next offset as this comes
        into view — and a short page is the only thing that says there is no more.
      */}
      {messages.length > 0 ? <EndOfList onReach={onReachEnd} loading={loadingMore} hasMore={hasMore} /> : null}
    </div>
  )
}

/**
 * The sentinel that asks for the next page.
 *
 * An observer rather than a scroll handler: it fires once when the element enters the viewport,
 * costs nothing while it is off screen, and needs no knowledge of the scroller's height — which
 * matters here because the list lives in its own scroll column, not the window.
 *
 * `rootMargin` starts the fetch a little before the bottom is actually reached, so the next page
 * is usually there by the time the reader gets to it.
 */
function EndOfList({ onReach, loading, hasMore }: { onReach?: () => void; loading: boolean; hasMore: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const onReachRef = useRef(onReach)
  onReachRef.current = onReach
  useEffect(() => {
    const node = ref.current
    if (!node || !hasMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onReachRef.current?.()
      },
      { rootMargin: '300px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore])
  return (
    <div ref={ref} className="pad" style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center' }}>
      {loading ? 'Loading older mail…' : hasMore ? '' : 'No older mail.'}
    </div>
  )
}
